/**
 * career-tree.ts
 *
 * Unified CSE-compatible career node tree for the Nigerian secondary school context.
 * Level 1: 3 broad nodes
 *   broad_stem_tech          — STEM, Health, Agriculture
 *   broad_business_finance   — Business, Finance, Economics
 *   broad_humanities_social  — Humanities, Arts, Social Sciences, Law
 * Level 2: ~35 field nodes as children of the broad nodes
 * Level 3: specializations — imported from spec tree files (see Step 4 below)
 *
 * Education & Training appears as a stub field node inside every broad so that
 * every career domain can produce teachers and trainers.
 *
 * trait.score is always 0 at definition time.
 * The Decision Engine seeds real scores from the student's generalTraits
 * when a node is activated.
 *
 * minimumThreshold guide:
 *   core trait    → ≥ 55
 *   support trait → ≥ 40
 *   nice-to-have  → ≥ 25
 */

import type { CareerPathNode, BroadCategory } from '../../Career Suggestion Engine/types';
import { STEM_TREE_WITH_TRAITS } from './career-trees/stemCareers';
import { HUMANITIES_TREE_WITH_TRAITS } from './career-trees/humanitiesArtsSocialSciencesCareers';
import { BUSINESS_TREE_WITH_TRAITS } from './career-trees/businessFinanceEconomicsCareers';

// ─── Helpers ──────────────────────────────────────────────────────────────────

type TraitDef = { traitId: string; name: string; minimumThreshold: number; score: 0 };

function t(traitId: string, name: string, minimumThreshold: number): TraitDef {
  return { traitId, name, score: 0, minimumThreshold };
}

/** Returns the Education & Training stub field node for a given broad. */
function makeEduStub(parentId: string, broadCategory: BroadCategory): CareerPathNode {
  return {
    nodeId: `field_edu_training_${parentId}`,
    name: 'Education & Training',
    alias: `${broadCategory}.EducationTraining`,
    level: 'field',
    broadCategory,
    parentId,
    description: 'Teaching, training, and curriculum roles across every field — every career can produce an educator.',
    traits: [
      t('T30', 'Patient Teaching',       55),
      t('T14', 'Empathy',                50),
      t('T49', 'Patience',               55),
      t('T17', 'Communication',          55),
      t('T23', 'Emotional Intelligence', 45),
    ],
    children: [
      {
        nodeId: `spec_edu_teacher_${parentId}`,
        name: 'Subject Teacher / Lecturer',
        alias: `${broadCategory}.EducationTraining.Teacher`,
        level: 'specialization',
        broadCategory,
        parentId: `field_edu_training_${parentId}`,
        description: 'Delivering curriculum in formal educational institutions from secondary to university level.',
        traits: [],
        children: [],
      },
      {
        nodeId: `spec_edu_trainer_${parentId}`,
        name: 'Corporate Trainer / Facilitator',
        alias: `${broadCategory}.EducationTraining.Trainer`,
        level: 'specialization',
        broadCategory,
        parentId: `field_edu_training_${parentId}`,
        description: 'Designing and delivering professional development programmes in industry and business.',
        traits: [],
        children: [],
      },
      {
        nodeId: `spec_edu_curriculum_${parentId}`,
        name: 'Curriculum Designer',
        alias: `${broadCategory}.EducationTraining.Curriculum`,
        level: 'specialization',
        broadCategory,
        parentId: `field_edu_training_${parentId}`,
        description: 'Building learning pathways, course materials, and assessment frameworks.',
        traits: [],
        children: [],
      },
      {
        nodeId: `spec_edu_coach_${parentId}`,
        name: 'Career & Skills Coach',
        alias: `${broadCategory}.EducationTraining.Coach`,
        level: 'specialization',
        broadCategory,
        parentId: `field_edu_training_${parentId}`,
        description: 'Guiding individuals to develop skills, plan careers, and achieve professional goals.',
        traits: [],
        children: [],
      },
    ],
  };
}

// ─── Health & Medical Sciences (under STEM) ───────────────────────────────────

const HEALTH_FIELDS: CareerPathNode[] = [
  {
    nodeId: 'field_health_medicine',
    name: 'Medicine & Surgery',
    alias: 'STEM.Medicine',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Diagnosing and treating illness — the highest-stakes application of scientific knowledge and human empathy.',
    traits: [
      t('T45', 'Clinical Reasoning',    65),
      t('T01', 'Analytical Thinking',   60),
      t('T47', 'Attention to Detail',   65),
      t('T14', 'Empathy',               55),
      t('T49', 'Patience',              55),
      t('T31', 'Memorization',          55),
      t('T44', 'Stress Management',     50),
    ],
    children: [],
  },
  {
    nodeId: 'field_health_nursing',
    name: 'Nursing & Midwifery',
    alias: 'STEM.Nursing',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Caring for patients across every stage of life — the backbone of every healthcare system.',
    traits: [
      t('T14', 'Empathy',                  65),
      t('T15', 'User Empathy',             60),
      t('T23', 'Emotional Intelligence',   55),
      t('T49', 'Patience',                 55),
      t('T30', 'Patient Teaching',         50),
      t('T44', 'Stress Management',        50),
    ],
    children: [],
  },
  {
    nodeId: 'field_health_pharmacy',
    name: 'Pharmacy',
    alias: 'STEM.Pharmacy',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Ensuring safe and effective use of medicines — bridging chemistry, biology, and patient care.',
    traits: [
      t('T01', 'Analytical Thinking',   60),
      t('T47', 'Attention to Detail',   65),
      t('T31', 'Memorization',          60),
      t('T07', 'Mathematical Thinking', 45),
      t('T45', 'Clinical Reasoning',    50),
      t('T48', 'Discipline',            55),
    ],
    children: [],
  },
  {
    nodeId: 'field_health_public_health',
    name: 'Public Health & Epidemiology',
    alias: 'STEM.PublicHealth',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Protecting the health of populations — tracking disease, designing interventions, and shaping health policy.',
    traits: [
      t('T11', 'Research Aptitude',      55),
      t('T19', 'Social Awareness',       60),
      t('T10', 'Statistical Reasoning',  50),
      t('T14', 'Empathy',                50),
      t('T17', 'Communication',          55),
      t('T32', 'Holistic Thinking',      50),
    ],
    children: [],
  },
  {
    nodeId: 'field_health_veterinary',
    name: 'Veterinary Medicine',
    alias: 'STEM.Veterinary',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Diagnosing and treating animals — crucial to agriculture, wildlife conservation, and food security.',
    traits: [
      t('T45', 'Clinical Reasoning',        55),
      t('T14', 'Empathy',                   60),
      t('T25', 'Kinesthetic Intelligence',  50),
      t('T29', 'Manual Dexterity',          45),
      t('T49', 'Patience',                  55),
      t('T47', 'Attention to Detail',       55),
    ],
    children: [],
  },
];

// ─── Agriculture & Environment (under STEM) ───────────────────────────────────

const AGRIC_FIELDS: CareerPathNode[] = [
  {
    nodeId: 'field_agric_agronomy',
    name: 'Agronomy & Crop Science',
    alias: 'STEM.Agronomy',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Improving food crop yield and resilience — the science that feeds Nigeria and the world.',
    traits: [
      t('T11', 'Research Aptitude',         55),
      t('T25', 'Kinesthetic Intelligence',  55),
      t('T49', 'Patience',                  60),
      t('T47', 'Attention to Detail',       55),
      t('T29', 'Manual Dexterity',          50),
      t('T05', 'Mechanical Aptitude',       45),
    ],
    children: [],
  },
  {
    nodeId: 'field_agric_animal_science',
    name: 'Animal & Livestock Science',
    alias: 'STEM.AnimalScience',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Raising and improving livestock for food, fibre, and agricultural productivity.',
    traits: [
      t('T14', 'Empathy',                   60),
      t('T25', 'Kinesthetic Intelligence',  55),
      t('T49', 'Patience',                  60),
      t('T11', 'Research Aptitude',         55),
      t('T47', 'Attention to Detail',       50),
      t('T45', 'Clinical Reasoning',        45),
    ],
    children: [],
  },
  {
    nodeId: 'field_agric_environment',
    name: 'Environmental Science & Management',
    alias: 'STEM.Environment',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Protecting ecosystems and natural resources — from climate change to pollution management.',
    traits: [
      t('T11', 'Research Aptitude',  60),
      t('T19', 'Social Awareness',   55),
      t('T32', 'Holistic Thinking',  55),
      t('T12', 'Curiosity',          55),
      t('T36', 'Risk Assessment',    50),
      t('T06', 'Systems Thinking',   50),
    ],
    children: [],
  },
  {
    nodeId: 'field_agric_food_science',
    name: 'Food Science & Technology',
    alias: 'STEM.FoodScience',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Developing safe, nutritious, and commercially viable food products.',
    traits: [
      t('T47', 'Attention to Detail',  60),
      t('T45', 'Clinical Reasoning',   50),
      t('T21', 'Aesthetic Sense',      50),
      t('T01', 'Analytical Thinking',  50),
      t('T29', 'Manual Dexterity',     55),
      t('T49', 'Patience',             55),
    ],
    children: [],
  },
  {
    nodeId: 'field_agric_fisheries',
    name: 'Fisheries & Marine Science',
    alias: 'STEM.Fisheries',
    level: 'field',
    broadCategory: 'STEM',
    parentId: 'broad_stem_tech',
    description: 'Managing aquatic resources and fish farming — vital to food security and coastal livelihoods.',
    traits: [
      t('T25', 'Kinesthetic Intelligence',  55),
      t('T11', 'Research Aptitude',         55),
      t('T49', 'Patience',                  60),
      t('T05', 'Mechanical Aptitude',       45),
      t('T06', 'Systems Thinking',          50),
      t('T47', 'Attention to Detail',       50),
    ],
    children: [],
  },
];

// ─── Broad nodes (Level 1) — 3 consolidated broads ───────────────────────────

export const CAREER_TREE: CareerPathNode[] = [
  {
    nodeId: 'broad_stem_tech',
    name: 'STEM & Technology',
    alias: 'STEM',
    level: 'broad',
    broadCategory: 'STEM',
    parentId: null,
    description: 'Mathematics, engineering, computing, sciences, health, and agriculture — building and understanding the technical world.',
    traits: [
      t('T01', 'Analytical Thinking',   40),
      t('T02', 'Logical Reasoning',     40),
      t('T07', 'Mathematical Thinking', 40),
      t('T12', 'Curiosity',             35),
    ],
    children: [
      // Rich spec fields from stemCareers.ts (technology, engineering, science, mathematics)
      ...STEM_TREE_WITH_TRAITS.children,
      // Health & medical fields (no dedicated spec file yet)
      ...HEALTH_FIELDS,
      // Agriculture & environment fields (no dedicated spec file yet)
      ...AGRIC_FIELDS,
      makeEduStub('broad_stem_tech', 'STEM'),
    ],
  },
  {
    nodeId: 'broad_business_finance',
    name: 'Business & Finance',
    alias: 'Business',
    level: 'broad',
    broadCategory: 'Business',
    parentId: null,
    description: 'Accounting, banking, marketing, economics, and entrepreneurship — creating and managing economic value.',
    traits: [
      t('T37', 'Numerical Reasoning', 35),
      t('T34', 'Strategic Thinking',  35),
      t('T42', 'Integrity',           35),
    ],
    children: [
      // Rich spec fields from businessFinanceEconomicsCareers.ts
      ...BUSINESS_TREE_WITH_TRAITS.children,
      makeEduStub('broad_business_finance', 'Business'),
    ],
  },
  {
    nodeId: 'broad_humanities_social',
    name: 'Humanities, Arts & Social Sciences',
    alias: 'Humanities',
    level: 'broad',
    broadCategory: 'Humanities',
    parentId: null,
    description: 'Communication, arts, psychology, law, governance, and social sciences — understanding people and the societies they build.',
    traits: [
      t('T17', 'Communication',    35),
      t('T14', 'Empathy',          35),
      t('T19', 'Social Awareness', 35),
    ],
    children: [
      // Rich spec fields from humanitiesArtsSocialSciencesCareers.ts (arts, humanities, law)
      ...HUMANITIES_TREE_WITH_TRAITS.children,
      makeEduStub('broad_humanities_social', 'Humanities'),
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Get a broad node by ID */
export function getBroadNode(nodeId: string): CareerPathNode | undefined {
  return CAREER_TREE.find(n => n.nodeId === nodeId);
}

/** Get a field node by ID (searches all children) */
export function getFieldNode(nodeId: string): CareerPathNode | undefined {
  for (const broad of CAREER_TREE) {
    const found = broad.children.find(c => c.nodeId === nodeId);
    if (found) return found;
  }
  return undefined;
}

/** Get all field nodes that belong to a given broad node */
export function getFieldsForBroad(broadNodeId: string): CareerPathNode[] {
  return getBroadNode(broadNodeId)?.children ?? [];
}

/** Flat list of every node in the tree (broad + field + specialization) */
export function getAllNodes(): CareerPathNode[] {
  return CAREER_TREE.flatMap(broad => [
    broad,
    ...broad.children.flatMap(field => [field, ...field.children]),
  ]);
}
